import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid,
  Button,
  Box,
  IconButton,
  Tooltip,
  Chip,
  alpha,
  useTheme,
  Stack,
  CircularProgress,
  Container
} from "@mui/material";
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Launch as LaunchIcon,
  Visibility as VisibilityIcon
} from "@mui/icons-material";
import { fetchWhatsNew, deleteEntry } from "../../api/whatsNewApi";
import WhatsNewFormDialog from "../../components/WhatsNewFormDialog";
import CustomSnackbarAlert from "../../components/customAlert";
import { useNavigate } from "react-router-dom";

const WhatsNewCardView = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [whatsNewData, setWhatsNewData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [createOrUpdate, setCreateOrUpdate] = useState("create");
  const [selectedEntry, setSelectedEntry] = useState(null);

  // for Alert
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [showAlert, setShowAlet] = useState(false);

  const getData = async () => {
    setLoading(true);
    try {
      const data = await fetchWhatsNew();
      setWhatsNewData(data);
    } catch (error) {
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleCloseSnackbar = () => {
    setShowAlet(false);
  };

  const handleSuccess = (mes) => {
    setOpenDialog(false);
    setAlertMessage(mes);
    setAlertSeverity("success");
    setShowAlet(true);
    getData();
  };

  const handleOpenDialogForCreate = () => {
    setCreateOrUpdate("create");
    setSelectedEntry(null);
    setOpenDialog(true);
  };

  const handleOnCardClick = (id) => {
    navigate(`/whatsNew?id=${id}`);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this entry?")) {
      const data = await deleteEntry(id);
      if (data.success) {
        setAlertMessage(data.message);
        setAlertSeverity("success");
        setShowAlet(true);
        getData();
      } else {
        setAlertMessage(data.error);
        setAlertSeverity("error");
        setShowAlet(true);
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.5 }}>
            What's New
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage web and mobile feature announcements
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialogForCreate}
          sx={{ borderRadius: 2, px: 3, py: 1 }}
        >
          Create New
        </Button>
      </Box>

      {error ? (
        <Typography variant="h6" color="error" textAlign="center">{error}</Typography>
      ) : whatsNewData.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <Typography variant="h6" color="text.secondary">No announcements found</Typography>
          <Button variant="outlined" sx={{ mt: 2 }} onClick={handleOpenDialogForCreate}>Add first entry</Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {whatsNewData.map((item) => (
            <Grid item key={item._id} xs={12} sm={6} md={4} lg={3}>
              <Card
                sx={{
                  height: "100%",
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: "pointer",
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 20px -10px rgb(0 0 0 / 0.15)'
                  },
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
                onClick={() => handleOnCardClick(item._id)}
              >
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="180"
                    image={item.webImageURL}
                    alt={item.title}
                    sx={{ bgcolor: 'grey.100' }}
                  />
                  <Box sx={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: 0.5 }}>
                    <Chip 
                      label={item.active ? "Active" : "Hidden"} 
                      size="small" 
                      color={item.active ? "success" : "default"}
                      sx={{ fontWeight: 700, backdropFilter: 'blur(4px)', bgcolor: alpha(item.active ? theme.palette.success.main : theme.palette.grey[500], 0.9), color: 'white' }}
                    />
                  </Box>
                </Box>
                <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2, mb: 1 }}>
                      {item.title}
                    </Typography>
                    <Chip label={item.type.toUpperCase()} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                  </Stack>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      minHeight: 40,
                      mb: 2
                    }}
                  >
                    {item.description}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600 }}>
                    FEATURE DATE: {new Date(item.featureDate).toLocaleDateString()}
                  </Typography>
                </CardContent>
                <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                  <Button 
                    fullWidth 
                    variant="light" 
                    size="small" 
                    startIcon={<VisibilityIcon size={16} />}
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), color: 'primary.main', fontWeight: 700 }}
                  >
                    View
                  </Button>
                  <Tooltip title="Delete Announcement">
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={(e) => handleDelete(e, item._id)}
                      sx={{ bgcolor: alpha(theme.palette.error.main, 0.05) }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Entry Form Dialog for Create/Update */}
      <WhatsNewFormDialog
        open={openDialog}
        onClose={handleCloseDialog}
        createOrUpdate={createOrUpdate}
        entry={selectedEntry}
        onSuccess={handleSuccess}
      />
      
      {/* CustomAlert component */}
      <CustomSnackbarAlert
        message={alertMessage}
        severity={alertSeverity}
        open={showAlert}
        onClose={handleCloseSnackbar}
      />
    </Container>
  );
};

export default WhatsNewCardView;

